import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from "react-hook-form";
import { Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import { MultiSelect } from "react-multi-select-component";
import { _detailState as _storage, formOptions } from '../_states/_detailState';
import { useGetData, getAddressData } from '../Services/_services';
import { config } from '../Services/_config';
import SelectOption from '../helpers/_SelectOption';
import ErrorMessage from '../helpers/_ErrorMessage';
import { yupResolver } from '@hookform/resolvers/yup';
import { RemainingCharacters } from '../helpers/_RemainingCharacters';
import LoadingSpinner from '../helpers/_Loading';

import { parse, isValid, format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import addYears from 'date-fns/addYears'
import { UtrnValidation } from './_UtrnValidation';

function Detail(props) {
  const { control, register, handleSubmit, formState: { errors, touchedFields }, trigger, setValue, resetField } = useForm({mode:"onTouched", resolver:yupResolver(formOptions)})
  //////States
  const [_states, setStates] = useState({
    foreName: _storage.getForeName(),
    surName: _storage.getSurName(),
    gender: _storage.getGender(),
    dob: _storage.getDob(),
    dobDay: _storage.getDobDay(),
    dobMonth: _storage.getDobMonth(),
    dobYear: _storage.getDobYear(),
    approximateAge: _storage.getApproximateAge(),
    addressYesOrNo: _storage.getAddressYesOrNo(),
    isAddressInLeeds: _storage.getIsAddressInLeeds(),
    postCode: _storage.getPostCode(),
    ddlAddress: _storage.getDdlAddress(),
    addressUPRN: _storage.getAddressUPRN(),
    isAddressManual: _storage.getIsAddressManual(),
    manualAddressInput: _storage.getManualAddressInput(),
    personAddressInfo: _storage.getPersonAddressInfo(),
    addressDetail: _storage.getAddressDetail(),
    emailYesOrNo: _storage.getEmailYesOrNo(),
    personsEmail: _storage.getPersonsEmail(),
    mainContactYesOrNo: _storage.getMainContactYesOrNo(),
    personsMainContactNo: _storage.getPersonsMainContactNo(),
    alternateContactYesOrNo: _storage.getAlternateContactYesOrNo(),
    alternateContactNo: _storage.getAlternateContactNo(),
    alternateContactNoDetail: _storage.getAlternateContactNoDetail(),
    nhsNumber: _storage.getNhsNumber(),
    ethnicOrigin: _storage.getEthnicOrigin(),
    languageSpoken: _storage.getLanguageSpoken(),
    interpreter: _storage.getInterpreter(),
    communicationNeeds: _storage.getCommunicationNeeds(),
    communicationNeedsDetail: _storage.getCommunicationNeedsDetail(),
    healthConditions: _storage.getHealthConditions()
  });

  const [healthConditions, setHealthConditions] = useState(_storage.getHealthConditions());
  const showHideElements = {
    showHideAddressYes: _storage.getAddressYesOrNo() === "yes" ? true : false,
    showHideAddressNo: _storage.getAddressYesOrNo() === "no" ? true : false,
    showHidePersonsEmail: _storage.getEmailYesOrNo() === "yes" ? true : false,
    showHidePersonsMainContactNo: _storage.getMainContactYesOrNo() === "yes" ? true : false,
    showHidePersonsAlternateContactNo: _storage.getAlternateContactYesOrNo() === "yes" ? true : false,
    showHideCommunicationNeedsDetail: _storage.getCommunicationNeeds() === "yes" ? true : false,
    showHideIsAddressInLeeds: _storage.getIsAddressInLeeds() === "yes" ? true : false,
  };

  const [ddlAddressHasValue, setDdlAddressHasValue] = useState(false);
  const [manualAddressInputHasValue, setManualAddressInputHasValue] = useState(false);
  const [addresses, setAddresses] = useState(null);
  const [isLoading,setIsLoading] = useState(false);
  const [showUnder18Alert,setShowUnder18Alert] = useState(false);
  /////End

  const { data: ethinicOrigin, loading: ethinicLoading } = useGetData(config.cisApis.ethinicOriginPath);
  const { data: languages, languageLoading } = useGetData(config.cisApis.languageSpokenPath);
  const { data: healthConditionsData, healthConditionLoading } = useGetData(config.cisApis.healthConditionsPath);

  // scroll to the top
  useEffect(() => {
    window.scrollTo(0, 0);

    setDdlAddressHasValue(_states.ddlAddress !== '' ? true : false);
    setManualAddressInputHasValue(_states.isAddressManual);


    //setValue responsible for back navigation using previous and next button
    //To solve a validation issue, set the value of the react-hook-form input "postCode" on page render, to retrieve data held in state
    //caused by value being needed but not being populated on page back navigation
    //https://react-hook-form.com/api/useform/setvalue
    setValue("postCode", _states.postCode);

    //Manually setting value and shouldTouch value for dob inputs (dobDay, dobMonth, dobYear)
    //to have validation to work "unobtrusively" for when returning to the page after having navigated to another page
    //setValue - https://react-hook-form.com/api/useform/setvalue/
    //touched fields - https://react-hook-form.com/api/useform/formstate/
    if(_states.dob) {
      setValue("dobDay", _states.dobDay, { shouldTouch: true });
      setValue("dobMonth", _states.dobMonth, { shouldTouch: true });
      setValue("dobYear", _states.dobYear, { shouldTouch: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Method to set the value of dob storage object when all dob entries contain a value (and will be valid)
  useEffect(() => {
    if(_states.dobDay && _states.dobMonth && _states.dobYear && !errors.dob) {
      const dateInput = parse(`${_states.dobDay}/${_states.dobMonth}/${_states.dobYear}`,'P', new Date(), { locale: enGB });
      if (isValid(dateInput)) {
        _storage.setDob(format(dateInput, 'dd-MM-yyyy'));

        const under18Date = addYears(new Date(), -18);
        setShowUnder18Alert(dateInput > under18Date);
      }
    }
  },[_states.dobDay, _states.dobMonth, _states.dobYear, errors.dob])

  //Custom effect for manually triggering the validation for dob field
  //When all dob fields have been "touched" (https://react-hook-form.com/api/useformstate/), manually trigger the validation
  //Aim of method is to prevent validation firing
  useEffect(() => {
    if((touchedFields.dobDay) && (touchedFields.dobMonth) && ((touchedFields.dobYear))) {
      trigger("dob");
      trigger("approximateAge");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[_states.dobDay, _states.dobMonth, _states.dobYear, touchedFields.dobDay, touchedFields.dobMonth, touchedFields.dobYear])

  UtrnValidation();

  const navigate = useNavigate();

  const handleResetAddress = () => {
    setStates({ ..._states, ddlAddress: null, isAddressManual:false, manualAddressInput:'' });
    setDdlAddressHasValue(false);
    setManualAddressInputHasValue(false);

    resetField("ddlAddress");
    _storage.setDdlAddress('');
    _storage.setIsAddressManual(false);
    _storage.setManualAddressInput('');
}

  const searchAddress = (searchKey) => {
    if (searchKey.length > 0) {
      setIsLoading(true);
      getAddressData(searchKey)
        .then(result =>  {
          setAddresses(result.response);
          setIsLoading(false);
          if (result.response.length === 0) {
            setStates({ ..._states, isAddressManual:true });
            _storage.setIsAddressManual(true);
          }
        });
    }
  }

  const handleButtonSearch = () => {
    setAddresses([]);
    searchAddress(_states.postCode.replaceAll(" ", ""));
    setStates({ ..._states, isAddressManual: false })
    _storage.setIsAddressManual(false);
  }

  const onSubmit = event => {
    navigate('/reason-for-referral');
  };

  return (
    <form className="input-form" onSubmit={handleSubmit(onSubmit)}>
      <main id="main" className="sectionOne">
        <div className="container-fluid">
          <div className="row">
						<div className="col-md-8">
              <div className="tab-content" id="myTabContent">
                <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
                  <h2>Details of the person being referred</h2>
                  <p>Please provide details of the person whom you feel has social care needs.</p>
                  <p>All fields marked * are mandatory and need to be completed so we can get the best outcome.</p>

									<div className={errors.foreName ? 'form-group form-group--error' : 'form-group'}>
                    <label htmlFor="foreName" className="form-label">Forename*</label>
                    <ErrorMessage id="foreName" error={errors.foreName}/>
                    <Controller name="foreName" control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <input {...field} type="text" id="foreName" name="foreName" value={_states.foreName}
													className={`form-control ${errors.foreName ? 'is-invalid' : ''}`}
													aria-invalid={errors.foreName ? "true" : "false"}
													aria-describedby={errors.foreName ? "foreName-error" : ""}
                          {...register('foreName')} onChange={(e) => {
                            onChange(e.target.value);
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setForeName(e.target.value);
                          }}
                        />
                      )}
                    />
                  </div>

									<div className={errors.surName ? 'form-group form-group--error' : 'form-group'}>
                    <label htmlFor="surName" className="form-label">Surname*</label>
                    <ErrorMessage id="surName" error={errors.surName}/>
                    <Controller name="surName" control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <input {...field} type="text" id="surName" name="surName" value={_states.surName}
													className={`form-control ${errors.surName ? 'is-invalid' : ''}`}
													aria-invalid={errors.surName ? "true" : "false"}
													aria-describedby={errors.surName ? "surName-error" : ""}
                          {...register('surName')} onChange={(e) => {
                            onChange(e.target.value);
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setSurName(e.target.value);
                          }}
                        />
                      )}
                    />
                  </div>

									<div className={errors.gender ? 'form-group form-group--error' : 'form-group'}>
                    <fieldset aria-describedby={errors.gender ? "gender-error" : ""} >
                      <legend className="sr-only">Gender</legend>
                      <label htmlFor="gender" className="form-label">Gender*</label>
                      <FormControl>
                        <ErrorMessage id="gender" error={errors.gender}/>
                        <RadioGroup {...register('gender')} name="gender" id="gender" value={_states.gender}
                          onChange={(e) => {
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setGender(e.target.value);
                          }}
                        >
                          <FormControlLabel {...register('gender')} value="male" control={<Radio />} label="Male (including Trans Male)" />
                          <FormControlLabel {...register('gender')} value="female" control={<Radio />} label="Female (including Trans Female)" />
                          <FormControlLabel {...register('gender')} value="nonbinary" control={<Radio />} label="Non Binary" />
                          <FormControlLabel {...register('gender')} value="other" control={<Radio />} label="Other" />
                          <FormControlLabel {...register('gender')} value="unknown" control={<Radio />} label="Unknown" />
                        </RadioGroup>
                      </FormControl>
                    </fieldset>
                  </div>

									<div className={errors.dob ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.dob ? "dob-error" : ""}>
                      <legend>
                          <span className="form-label-bold">Date of birth</span>
                          <span className="form-hint">For example, 31 3 1970. If you don’t know the date of birth, please put their approximate age in the next question</span>
                      </legend>

                      <ErrorMessage id="dob" error={errors.dob}/>
                      { showUnder18Alert &&
                        <div className="panel panel-border-narrow" role="alert">
                          <p>This referral is for someone under the age of 18. Unless this is a referral for the transitions team, please refer to Childrens & Families.</p>
                        </div>
                      }

                      <div className="form-date">
                        <div className="form-group form-group-day">
                          <label htmlFor="dobDay" className="form-span">Day</label>
                          <Controller name="dobDay" control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                              <input {...field} maxLength="2" type="text" id="dobDay" name="dobDay" value={_states.dobDay} className={`form-control ${errors.dobDay ? 'is-invalid' : ''}`}
                                {...register('dobDay')} onChange={(e) => {
                                  onChange(e.target.value);
                                  const { value } = e.target;
                                  const re = /^[0-9]+$/;
                                  if (value === "" || re.test(e.target.value)) {
                                    setStates({ ..._states, [e.target.name]: e.target.value })
                                    _storage.setDobDay(e.target.value);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>

                        <div className="form-group form-group-month">
                          <label htmlFor="dobMonth" className="form-span">Month</label>
                          <Controller name="dobMonth" control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                              <input {...field} maxLength="2" type="text" id="dobMonth" name="dobMonth" value={_states.dobMonth} className={`form-control ${errors.dobMonth ? 'is-invalid' : ''}`}
                                {...register('dobMonth')} onChange={(e) => {
                                  onChange(e.target.value);
                                  const { value } = e.target;
                                  const re = /^[0-9]+$/;
                                  if (value === "" || re.test(e.target.value)) {
                                    setStates({ ..._states, [e.target.name]: e.target.value })
                                    _storage.setDobMonth(e.target.value);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className="form-group form-group-year">
                          <label htmlFor="dobYear" className="form-span">Year</label>
                          <Controller name="dobYear" control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                              <input {...field} maxLength="4" type="text" id="dobYear" name="dobYear" value={_states.dobYear} className={`form-control ${errors.dobYear ? 'is-invalid' : ''}`}
                                {...register('dobYear')} onChange={(e) => {
                                  onChange(e.target.value);
                                  const { value } = e.target;
                                  const re = /^[0-9]+$/;
                                  if (value === "" || re.test(e.target.value)) {
                                    setStates({ ..._states, [e.target.name]: e.target.value })
                                    _storage.setDobYear(e.target.value);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </fieldset>
                  </div>

									<div className={errors.approximateAge ? 'form-group form-group--error' : 'form-group'}>
                    <label htmlFor="approximateAge" className="form-label">Approximate age</label>
                    <p>If you don’t know the date of birth, please put their approximate age</p>
                    <ErrorMessage id="approximateAge" error={errors.approximateAge}/>
                    <Controller name="approximateAge" control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <input {...field} maxLength="3" type="text" id="approximateAge" name="approximateAge" value={_states.approximateAge}
													className={`form-control ${errors.approximateAge ? 'is-invalid' : ''}`}
													aria-invalid={errors.approximateAge ? "true" : "false"}
													aria-describedby={errors.approximateAge ? "approximateAge-error" : ""}
                          {...register('approximateAge')} onChange={(e) => {
                            onChange(e.target.value);
                            const { value } = e.target;
                            const re = /^[0-9]+$/;
                            if (value === "" || re.test(e.target.value)) {
                              setStates({ ..._states, [e.target.name]: e.target.value })
                              _storage.setApproximateAge(e.target.value);
                              _storage.setDob("");
                              // Manually trigger validation for dob to trigger the test function for checking date is valid
                              trigger("dob");
                            }
                          }}
                        />
                      )}
                    />
                  </div>

									<div className={errors.addressYesOrNo ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.addressYesOrNo ? "addressYesOrNo-error" : ""}>
                      <div className="mb-3">
                        <label htmlFor="addressYesOrNo" className="form-label">Do you know the address of the person being referred?*</label>
                        <FormControl>
                          <ErrorMessage id="addressYesOrNo" error={errors.addressYesOrNo}/>
                          <RadioGroup {...register('addressYesOrNo')} name="addressYesOrNo" id="addressYesOrNo" value={_states.addressYesOrNo} row
                            onChange={(e) => {
                              setStates({ ..._states, [e.target.name]: e.target.value })
                              _storage.setAddressYesOrNo(e.target.value);
                            }}
                          >
                            <FormControlLabel {...register('addressYesOrNo')} value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel {...register('addressYesOrNo')} value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>
                      </div>


                      { showHideElements.showHideAddressYes &&
                      <>
                        <div className="panel panel-border-narrow js-hidden" id="know-address-yes-input">

													<div className={errors.isAddressInLeeds ? 'form-group form-group--error' : 'form-group'}>
														<fieldset aria-describedby={errors.isAddressInLeeds ? "isAddressInLeeds-error" : ""}>
															<label htmlFor="isAddressInLeeds" className="form-label">Is the address in Leeds?*</label>
															<FormControl>
																<ErrorMessage id="isAddressInLeeds" error={errors.isAddressInLeeds}/>
																<RadioGroup {...register('isAddressInLeeds')} name="isAddressInLeeds" id="isAddressInLeeds" value={_states.isAddressInLeeds} row
																	onChange={(e) => {
																		setStates({ ..._states, [e.target.name]: e.target.value })
																		_storage.setIsAddressInLeeds(e.target.value);
																	}}
																	>
																	<FormControlLabel {...register('isAddressInLeeds')} value="yes" control={<Radio />} label="Yes" />
																	<FormControlLabel {...register('isAddressInLeeds')} value="no" control={<Radio />} label="No" />
																</RadioGroup>
															</FormControl>
														</fieldset>
                          </div>

                          { _states.isAddressInLeeds === 'yes' &&
                            <>
                               {
                               ((ddlAddressHasValue || manualAddressInputHasValue)) ?
                                    // Selected address input
                                    <div className="form-group">
                                      <label className="form-label form-label-bold">Selected address: </label>
                                      <p>{ ddlAddressHasValue ? _states.ddlAddress : _states.manualAddressInput }</p>
                                      <a role="button" onClick={() => {
                                        handleResetAddress();
                                      }} className="link--simple">Change address</a>
                                    </div>
                                    // End of Selected address input
                                :
                                <>
                                  {/* Postcode textbox entry */}
																	<div className={errors.postCode ? 'form-group form-group--error' : 'form-group'}>
                                    <label htmlFor="postCode" className="form-label">What address does the person live at?*</label>
                                    <p>Enter the postcode</p>
                                    <ErrorMessage id="postCode" error={errors.postCode}/>
                                    <Controller name="postCode" control={control}
                                      render={({ field: { value, onChange, ...field } }) => (
                                        <input {...field} maxLength="20" type="text" id="postCode" name="postCode" value={_states.postCode}
																					className={`form-control ${errors.postCode ? 'is-invalid' : ''}`}
																					aria-invalid={errors.postCode ? "true" : "false"}
																					aria-describedby={errors.postCode ? "postCode-error" : ""}
                                          {...register('postCode')} onChange={(e) => {
                                            onChange(e.target.value.toUpperCase());
                                            setStates({ ..._states, [e.target.name]: e.target.value.toUpperCase() })
                                            _storage.setPostCode(e.target.value.toUpperCase());
                                          }}
                                        />
                                      )}
                                    />
                                  </div>
                                  {/* End of Postcode textbox entry */}

                                  {/* Find address button */}
                                  <div className="mt-lg-4">
                                    <button onClick={() => {
                                      handleButtonSearch();
                                      //Manually trigger postcode validation because the postcode entry needs a value for when this button is pressed
                                      //Reason being this is a component inside a form - no nested forms
                                      trigger("postCode");
                                    }} className="btn btn-primary btn-lg" type="button">Find address</button>
                                  </div>
                                  {/* End of Find address button */}

                                  { isLoading ? <LoadingSpinner isLoading={isLoading}/> :
                                    Array.isArray(addresses) ?
                                      addresses.length > 0 ?
                                        // List of addresses
                                          <div className="mt-lg-4">
                                            <div className="mb-3">
                                              <label htmlFor="ddlAddress" className="form-label">Select the address*</label>
                                              <ErrorMessage id="ddlAddress" error={errors.ddlAddress}/>
                                              <Controller name="ddlAddress" control={control}
                                                render={({ field: { value, onChange, ...field } }) => (
                                                  <select  {...field} id="ddlAddress" name="ddlAddress" value={_states.ddlAddress}
																										className={`form-control ${errors.ddlAddress ? 'is-invalid' : ''}`}
																										aria-invalid={errors.ddlAddress ? "true" : "false"}
																										aria-describedby={errors.ddlAddress ? "ddlAddress-error" : ""}
                                                    {...register('ddlAddress')} onChange={(e) => {
                                                      onChange(e.target.value);

                                                      const selectedAddress = addresses.filter(m => m?.uprn === e.target.value)[0];
                                                      setStates({ ..._states, ddlAddress: selectedAddress.name, addressUPRN: selectedAddress.uprn });
                                                      setDdlAddressHasValue(true);

                                                      _storage.setDdlAddress(selectedAddress.name);
                                                      _storage.setAddressUPRN(selectedAddress.uprn);
                                                    }}
                                                  >
                                                    <option value="">Please select</option>
                                                    {addresses.map((address) => (
                                                      <option key={address.uprn} value={address.uprn}>{address.name}</option>))}
                                                  </select>
                                                )}
                                              />
                                            </div>
                                          </div>
                                        // End of List of addresses
                                      :
                                        // No addresses text
                                         <>
                                          <div className="text">
                                            <div className="notice">
                                              <i className="fa fa-exclamation-triangle" aria-hidden="true">
                                                <span className="sr-only">Warning</span>
                                              </i>
                                              <strong className="bold-small">No addresses found</strong>
                                            </div>
                                            <p>Please check that the Leeds postcode you entered is correct.</p>
                                            <p>If the address still can't be found and is within the Leeds City Council area, enter the details manually in the field below.</p>
                                          </div>

																					<div className={errors.manualAddressInput ? 'form-group form-group--error' : 'form-group'}>
                                            <ErrorMessage id="manualAddressInput" error={errors.manualAddressInput}/>
                                            <Controller name="manualAddressInput" control={control}
                                              render={({ field: { value, onChange, ...field } }) => (
                                                <textarea {...field} rows="4" id="manualAddressInput" name="manualAddressInput" value={_states.manualAddressInput}
																									className={`form-control ${errors.manualAddressInput ? 'is-invalid' : ''}`}
																									aria-invalid={errors.manualAddressInput ? "true" : "false"}
																									aria-describedby={errors.manualAddressInput ? "manualAddressInput-error" : ""}
                                                  {...register('manualAddressInput')} onChange={(e) => {
                                                    onChange(e.target.value);
                                                    setStates({ ..._states, [e.target.name]: e.target.value })
                                                    _storage.setManualAddressInput(e.target.value);
                                                  }}
                                                />
                                              )}
                                            />
                                            <p>You can enter up to 200 characters</p>
                                            <RemainingCharacters maxNoCharacters={200} inputValue={_states.manualAddressInput} />
                                          </div>

                                         </>
                                        // End of No addresses text
                                    : null
                                  }
                              </>
                              }
                            </>
                          }

                          { _states.isAddressInLeeds === 'no' &&
                            <>
                              <div className={errors.personAddressInfo ? 'form-group form-group--error' : 'form-group'}>
                                <label htmlFor="personAddressInfo" className="form-label">Tell us the full address. If this is someone else's address, please state their relationship to the person being referred.*</label>
                                <span className="form-hint">For example hostel, friend's house</span>
                                <ErrorMessage id="personAddressInfo" error={errors.personAddressInfo}/>
                                <Controller name="personAddressInfo" control={control}
                                  render={({ field: { value, onChange, ...field } }) => (
                                    <textarea {...field} rows="4" id="personAddressInfo" name="personAddressInfo" value={_states.personAddressInfo}
																			className={`form-control ${errors.personAddressInfo ? 'is-invalid' : ''}`}
																			aria-invalid={errors.personAddressInfo ? "true" : "false"}
																			aria-describedby={errors.personAddressInfo ? "personAddressInfo-error" : ""}
                                    {...register('personAddressInfo')} onChange={(e) => {
                                      onChange(e.target.value);
                                        setStates({ ..._states, [e.target.name]: e.target.value })
                                        _storage.setPersonAddressInfo(e.target.value);
                                    }}/>
                                  )}/>
                                  <p>You can enter up to 200 characters</p>
                                  <RemainingCharacters maxNoCharacters={200} inputValue={_states.personAddressInfo} />
                              </div>
                            </>
                          }
                        </div>
                      </>
                      }

                      {showHideElements.showHideAddressNo &&
                        <div className="panel panel-border-narrow js-hidden" id="know-address-no-input">
                          <label htmlFor="addressDetail" className="form-label">Please give details why the address is not known*</label>
                          <span className="form-hint">Example, the person is homeless</span>
                          <ErrorMessage id="addressDetail" error={errors.addressDetail}/>
                          <Controller name="addressDetail" control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                              <textarea {...field} rows="4" type="text" id="addressDetail" name="addressDetail" value={_states.addressDetail}
																className={`form-control ${errors.addressDetail ? 'is-invalid' : ''}`}
																aria-invalid={errors.addressDetail ? "true" : "false"}
																aria-describedby={errors.addressDetail ? "addressDetail-error" : ""}
                                {...register('addressDetail')} onChange={(e) => {
                                  onChange(e.target.value);
                                  _storage.setAddressDetail(e.target.value);
                                  setStates({ ..._states, [e.target.name]: e.target.value })
                                }}/>
                            )}/>
                          <p>You can enter up to 200 characters</p>
                          <RemainingCharacters maxNoCharacters={200} inputValue={_states.addressDetail} />
                        </div>
                      }
                    </fieldset>
                  </div>

									<div className={errors.emailYesOrNo ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.emailYesOrNo ? "emailYesOrNo-error" : ""}>
                      <legend className="sr-only">Do you know if the person has an email?</legend>
                      <label htmlFor="emailYesOrNo" className="form-label">Do you know if the person has an email?*</label>
                      <FormControl>
                        <ErrorMessage id="emailYesOrNo" error={errors.emailYesOrNo}/>
                        <RadioGroup {...register('emailYesOrNo')} name="emailYesOrNo" id="emailYesOrNo" value={_states.emailYesOrNo} row
                          onChange={(e) => {
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setEmailYesOrNo(e.target.value);
                          }}
                        >
                          <FormControlLabel {...register('emailYesOrNo')} value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel {...register('emailYesOrNo')} value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>

                      {showHideElements.showHidePersonsEmail &&
                        <div className="panel panel-border-narrow js-hidden" id="person-email-input">
                          <label htmlFor="personsEmail" className="form-label">Please enter person’s email*</label>
                          <ErrorMessage id="personsEmail" error={errors.personsEmail}/>
                          <Controller name="personsEmail" control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                              <input {...field} type="text" id="personsEmail" name="personsEmail" value={_states.personsEmail}
																className={`form-control ${errors.personsEmail ? 'is-invalid' : ''}`}
																aria-invalid={errors.personsEmail ? "true" : "false"}
																aria-describedby={errors.personsEmail ? "personsEmail-error" : ""}
                                {...register('personsEmail')} onChange={(e) => {
                                  onChange(e.target.value);
                                  setStates({ ..._states, [e.target.name]: e.target.value })
                                  _storage.setPersonsEmail(e.target.value);
                                }}/>
                              )}/>
                        </div>
                      }
                    </fieldset>
                  </div>

									<div className={errors.mainContactYesOrNo ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.mainContactYesOrNo ? "mainContactYesOrNo-error" : ""}>
                      <legend className="sr-only">Do you know the main contact number of person?</legend>
                      <label htmlFor="mainContactYesOrNo" className="form-label">Do you know the main contact number of person?*</label>
                      <p>This can either be a home number or mobile number</p>
                      <FormControl>
                        <ErrorMessage id="mainContactYesOrNo" error={errors.mainContactYesOrNo}/>
                        <RadioGroup {...register('mainContactYesOrNo')} name="mainContactYesOrNo" id="mainContactYesOrNo" value={_states.mainContactYesOrNo} row
                          onChange={(e) => {
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setMainContactYesOrNo(e.target.value);
                          }}
                        >
                          <FormControlLabel {...register('mainContactYesOrNo')} value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel {...register('mainContactYesOrNo')} value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>

                    {showHideElements.showHidePersonsMainContactNo &&
                      <div className="panel panel-border-narrow js-hidden" id="person-main-contact-input">
												<label htmlFor="personsMainContactNo" className="form-label">Please enter person’s number*</label>
                        <ErrorMessage id="personsMainContactNo" error={errors.personsMainContactNo}/>
                        <Controller name="personsMainContactNo" control={control}
                          render={({ field: { value, onChange, ...field } }) => (
                            <input {...field} type="text" id="personsMainContactNo" name="personsMainContactNo" value={_states.personsMainContactNo}
															className={`form-control ${errors.personsMainContactNo ? 'is-invalid' : ''}`}
															aria-invalid={errors.personsMainContactNo ? "true" : "false"}
															aria-describedby={errors.personsMainContactNo ? "personsMainContactNo-error" : ""}
                              {...register('personsMainContactNo')} onChange={(e) => {
                                onChange(e.target.value);
                                setStates({ ..._states, [e.target.name]: e.target.value })
                                _storage.setPersonsMainContactNo(e.target.value);
                              }}
                            />
                          )}
                        />
                      </div>
                    }
                    </fieldset>
                  </div>

									<div className={errors.alternateContactYesOrNo ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.alternateContactYesOrNo ? "alternateContactYesOrNo-error" : ""}>
                      <legend className="sr-only">Do you have an alternate contact number?</legend>
                      <label htmlFor="alternateContactYesOrNo" className="form-label">Do you have an alternate contact number?*</label>
                      <p>This could be a family member?</p>
                      <FormControl>
                        <ErrorMessage id="alternateContactYesOrNo" error={errors.alternateContactYesOrNo}/>
                        <RadioGroup {...register('alternateContactYesOrNo')} name="alternateContactYesOrNo" id="alternateContactYesOrNo" value={_states.alternateContactYesOrNo} row
                          onChange={(e) => {
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setAlternateContactYesOrNo(e.target.value);
                          }}
                        >
                          <FormControlLabel {...register('alternateContactYesOrNo')} value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel {...register('alternateContactYesOrNo')} value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>

                      {showHideElements.showHidePersonsAlternateContactNo &&
                        <div className="panel panel-border-narrow js-hidden" id="person-alternative-contact-input">
                          <label htmlFor="alternateContactNo" className="form-label">Please enter contact number*</label>
                            <ErrorMessage id="alternateContactNo" error={errors.alternateContactNo}/>
                            <Controller name="alternateContactNo" control={control}
                              render={({ field: { value, onChange, ...field } }) => (
                                <input {...field} type="text" id="alternateContactNo" name="alternateContactNo" value={_states.alternateContactNo}
																	className={`form-control ${errors.alternateContactNo ? 'is-invalid' : ''}`}
																	aria-invalid={errors.alternateContactNo ? "true" : "false"}
																	aria-describedby={errors.alternateContactNo ? "alternateContactNo-error" : ""}
                                  {...register('alternateContactNo')} onChange={(e) => {
                                    onChange(e.target.value);
                                    setStates({ ..._states, [e.target.name]: e.target.value })
                                    _storage.setAlternateContactNo(e.target.value);
                                  }}
                                />
                              )}
                            />

                            <div className="mt-lg-4">
                              <div className="mb-3">
                                <label htmlFor="alternateContactNoDetail" className="form-label">Please give details to the nature of contact*</label>
                                <ErrorMessage id="alternateContactNoDetail" error={errors.alternateContactNoDetail}/>
                                <Controller name="alternateContactNoDetail" control={control}
                                  render={({ field: { value, onChange, ...field } }) => (
                                    <textarea {...field} rows="4" type="text" id="alternateContactNoDetail" name="alternateContactNoDetail" value={_states.alternateContactNoDetail}
																			className={`form-control ${errors.alternateContactNoDetail ? 'is-invalid' : ''}`}
																			aria-invalid={errors.alternateContactNoDetail ? "true" : "false"}
																			aria-describedby={errors.alternateContactNoDetail ? "alternateContactNoDetail-error" : ""}
                                      {...register('alternateContactNoDetail')} onChange={(e) => {
                                        onChange(e.target.value);
                                        _storage.setAlternateContactNoDetail(e.target.value);
                                        setStates({ ..._states, [e.target.name]: e.target.value })
                                      }}
                                    />
                                  )}
                                />
                                <p>You can enter up to 200 characters</p>
                                <RemainingCharacters maxNoCharacters={200} inputValue={_states.alternateContactNoDetail} />
                              </div>
                            </div>
                          </div>
                      }
                    </fieldset>
                  </div>


									<div className={errors.nhsNumber ? 'form-group form-group--error' : 'form-group'}>
                    <label htmlFor="nhsNumber" className="form-label">NHS Number</label>
                    <p>This is a ten digit number and can help identify person</p>
                    <ErrorMessage id="nhsNumber" error={errors.nhsNumber}/>
                    <Controller name="nhsNumber" control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <input {...field} type="text" id="nhsNumber" name="nhsNumber" value={_states.nhsNumber}
													className={`form-control ${errors.nhsNumber ? 'is-invalid' : ''}`}
													aria-invalid={errors.nhsNumber ? "true" : "false"}
													aria-describedby={errors.nhsNumber ? "nhsNumber-error" : ""}
                          {...register('nhsNumber')} onChange={(e) => {
                            onChange(e.target.value);
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setNhsNumber(e.target.value);
                          }}
                        />
                      )}
                    />
                  </div>

									<div className={errors.ethnicOrigin ? 'form-group form-group--error' : 'form-group'}>
                    <label htmlFor="ethnicOrigin" className="form-label">Ethnic origin*</label>
                    <p>
                      Ethnic origin refers to members of an ethnic group who share the same cultural identity. This does not mean country of birth or nationality
                    </p>
                    <ErrorMessage id="ethnicOrigin" error={errors.ethnicOrigin}/>
                    <Controller name="ethnicOrigin" control={control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <select  {...field} id="ethnicOrigin" name="ethnicOrigin" value={_states.ethnicOrigin}
													className={`form-control ${errors.ethnicOrigin ? 'is-invalid' : ''}`}
													aria-invalid={errors.ethnicOrigin ? "true" : "false"}
													aria-describedby={errors.ethnicOrigin ? "ethnicOrigin-error" : ""}
                          {...register('ethnicOrigin')} onChange={(e) => {
                            onChange(e.target.value);
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setEthnicOrigin(e.target.value);
                          }}
                        >
                          <SelectOption isParent={true} data={ethinicOrigin} />
                        </select>
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="languageSpoken" className="form-label">Language spoken by person being referred</label>
                    <select id="languageSpoken" aria-label="Default select example" name="languageSpoken"
                      className="form-control" value={_states.languageSpoken}
                      onChange={(e) => {
                        setStates({ ..._states, [e.target.name]: e.target.value })
                        _storage.setLanguageSpoken(e.target.value);
                      }}
                    >
                      <SelectOption isParent={true} data={languages} />
                    </select>
                  </div>

									<div className={errors.interpreter ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.interpreter ? "interpreter-error" : ""}>
											<label htmlFor="interpreter" className="form-label">Does the person need an interpreter?*</label>
											<FormControl>
												<ErrorMessage id="interpreter" error={errors.interpreter}/>
												<RadioGroup {...register('interpreter')} name="interpreter" id="interpreter" value={_states.interpreter}
													onChange={(e) => {
														setStates({ ..._states, [e.target.name]: e.target.value })
														_storage.setInterpreter(e.target.value);
													}}
												>
													<FormControlLabel {...register('interpreter')} value="yes" control={<Radio />} label="Yes" />
													<FormControlLabel {...register('interpreter')} value="no" control={<Radio />} label="No" />
													<FormControlLabel {...register('interpreter')} value="dontknow" control={<Radio />} label="Don’t know" />
												</RadioGroup>
											</FormControl>
										</fieldset>
                  </div>

									<div className={errors.communicationNeeds ? 'form-group form-group--error' : 'form-group'}>
										<fieldset aria-describedby={errors.communicationNeeds ? "communicationNeeds-error" : ""}>
                      <legend className="sr-only">Does the person have any specific communication needs?</legend>
                      <label htmlFor="communicationNeeds" className="form-label">Does the person have any specific communication needs?*</label>
                      <p>For example, the person is hard of hearing or is unable to verbally communicate</p>
                      <FormControl>
                        <ErrorMessage id="communicationNeeds" error={errors.communicationNeeds}/>
                        <RadioGroup {...register('communicationNeeds')} name="communicationNeeds" id="communicationNeeds" value={_states.communicationNeeds}
                          onChange={(e) => {
                            setStates({ ..._states, [e.target.name]: e.target.value })
                            _storage.setCommunicationNeeds(e.target.value);
                          }}
                        >
                          <FormControlLabel {...register('communicationNeeds')} value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel {...register('communicationNeeds')} value="no" control={<Radio />} label="No" />
                          <FormControlLabel {...register('communicationNeeds')} value="dontknow" control={<Radio />} label="Don’t know" />
                        </RadioGroup>
                      </FormControl>

                    {showHideElements.showHideCommunicationNeedsDetail &&
                      <div className="panel panel-border-narrow js-hidden">
                        <label htmlFor="communicationNeedsDetail" className="form-label">If yes, please give details*</label>
                        <ErrorMessage id="communicationNeedsDetail" error={errors.communicationNeedsDetail}/>
                        <Controller name="communicationNeedsDetail" control={control}
                          render={({ field: { value, onChange, ...field } }) => (
                            <textarea {...field} rows="4" type="text" id="communicationNeedsDetail" name="communicationNeedsDetail" value={_states.communicationNeedsDetail}
															className={`form-control ${errors.communicationNeedsDetail ? 'is-invalid' : ''}`}
															aria-invalid={errors.communicationNeedsDetail ? "true" : "false"}
															aria-describedby={errors.communicationNeedsDetail ? "communicationNeedsDetail-error" : ""}
                              {...register('communicationNeedsDetail')} onChange={(e) => {
                                onChange(e.target.value);
                                _storage.setCommunicationNeedsDetail(e.target.value);
                                setStates({ ..._states, [e.target.name]: e.target.value })
                              }}
                            />
                          )}
                        />
                        <p>You can enter up to 200 characters</p>
                        <RemainingCharacters maxNoCharacters={200} inputValue={_states.communicationNeedsDetail} />
                      </div>
                    }
                    </fieldset>
                  </div>

									<div className={errors.healthConditions ? 'form-group form-group--error' : 'form-group'}>
                    <label htmlFor="healthConditions" id="healthConditions-label" className="form-label">Does the person have any pre-existing health conditions?*</label>
                    <p>You can choose more than one</p>
                    <ErrorMessage id="healthConditions" error={errors.healthConditions}/>
										<div className="js--select" style={{ width: '50%', fontSize: '19' }}>
                      <Controller name="healthConditions" control={control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <MultiSelect {...field} {...register('healthConditions')} id="healthConditions" labelledBy="healthConditions-label" name="healthConditions" value={_states.healthConditions}
                            valueRenderer={(value) => {
                              if (value.length === 0)
                                return <>Please select</>;
                            }}
                            onChange={(e) => {
                              onChange(e);
                              setStates({ ..._states, ['healthConditions']: e })
                              _storage.setHealthConditions(e);
                              // Manually trigger validation for healthConditions to trigger the test function for checking the input length
                              trigger("healthConditions");
                            }}
                            options={healthConditionsData.map((health) => {
                              return { label: health.description, value: health.code }
                            })}
                          >
                          </MultiSelect>
                        )}
                      />
                    </div>
                  </div>

									<div className="form-group form-group--progbuttons">
                    <button className="btn  btn-light btn-lg" type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/aboutyou');
                      }}
                    >Previous</button>
                    <button className="btn btn-primary btn-lg" type="submit">Next</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </form>
  );
};
export default Detail;
